import { Grid, useMediaQuery, useTheme } from '@mui/material';
import AuthService from 'utils/AuthService';
import {
  JobType,
  Property,
  PropertyType,
  Status,
  TimeZone,
  TraxeroActionResponse,
} from 'types';
import React, {
  ChangeEvent,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { TIME_ZONES } from 'utils';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import {
  useFetcher,
  useParams,
  useRevalidator,
  useRouteLoaderData,
} from 'react-router';

import { AppContext } from 'context/appContext';
import {
  PropertiesActionHandler,
  PropertyLoaderData,
} from 'pages/properties/PropertyLayout';
import TimeZoneSelect from 'components/inputs/TimeZoneSelect';
import AuturaTextField from 'components/inputs/AuturaTextField';
import { useTranslation } from 'react-i18next';
import AuturaDropdown from 'components/inputs/AuturaDropdown';
import { ValidatorForm } from 'react-material-ui-form-validator';
import DetailsHeader from 'components/layout/DetailsHeader';
import FormFrame from 'components/layout/FormFrame';
import SettingsSection from 'components/layout/SettingsSection';
import AuturaStateField from 'components/inputs/AuturaStateField';
import { ScrollableFormContainer } from 'components/layout/ScrollableContainer';
import { TAB_LAYOUT_HEADER_HEIGHT } from 'components/layout/FormTabNavigation';

import FilterLabelAutocomplete from 'components/autocompletes/FilterLabelAutocomplete';
import AuturaSwitch from 'components/inputs/AuturaSwitch';
import { ROUTES, routeWithParams } from 'router/routes';
import { JobTypeChecklist } from 'components/inputs/JobTypeChecklist';
import { useNavigate } from 'react-router';
import { ArrowBack, Close } from '@mui/icons-material';

const PropertyDetails = (): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const displayToast = useContext(ToastContext);
  const navigate = useNavigate();
  const fetcher = useFetcher();
  const revalidator = useRevalidator();
  const isMobile = useMediaQuery(useTheme().breakpoints.down('lg'));
  const { propertyID } = useParams();
  const { _property } = useRouteLoaderData(
    ROUTES.PROPERTY,
  ) as PropertyLoaderData;
  const [property, setProperty] = useState<Property | null>(null);
  const [timeZone, setTimeZone] = useState<TimeZone | null>(null);
  const tenantHasFindMyCarDisabled = !state.tenant.data?.enableFindMyCar;


  useEffect(() => {
    const awaitLoader = async () => {
      const _p = await _property;
      const p = { ..._p };

      // temporary to convert data to new format
      if (p.data?.settings?.requirePropertyManagerSignature) {
        p.data.settings.requirePMApproval = [JobType.TOW];
        p.data.settings.requirePropertyManagerSignature = false; // remove this in 3.4
      }
      setTimeZone(
        TIME_ZONES.find(
          (timeZone: TimeZone) =>
            timeZone.label === p.data.timeZone ||
            timeZone.timeZone === p.data.timeZone,
        ) ||
          TIME_ZONES.find(
            (timeZone: TimeZone) =>
              timeZone.timeZone === state.tenant.data?.timeZone,
          ) ||
          TIME_ZONES[5], // default to America/New_York
      );
      setProperty(p);
    };
    awaitLoader();
  }, [_property]);

  const response = fetcher.data as
    | undefined
    | TraxeroActionResponse<PropertiesActionHandler>;
  useEffect(() => {
    if (response) {
      if (response.status < 400) {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: t('update_success', {
            name: `${property?.name}`,
            ns: 'toasts',
          }),
        });
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: t('update_error', {
            name: `${property?.name}`,
            ns: 'toasts',
          }),
        });
      }
    }
  }, [fetcher]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!property) return;

    const { name, value } = event.target;
    const prevProperty = { ...property };
    switch (name) {
      case 'name':
        setProperty({ ...prevProperty, name: value });
        break;
      case 'description':
        setProperty({ ...prevProperty, description: value });
        break;
      case 'externalID':
        setProperty({ ...prevProperty, externalID: value });
        break;
      case 'street':
        setProperty({
          ...prevProperty,
          location: { ...prevProperty.location, street: value },
        });
        break;
      case 'city':
        setProperty({
          ...prevProperty,
          location: { ...prevProperty.location, city: value },
        });
        break;
      case 'stateCode':
        setProperty({
          ...prevProperty,
          location: { ...prevProperty.location, stateCode: value },
        });
        break;
      case 'postalCode':
        setProperty({
          ...prevProperty,
          location: { ...prevProperty.location, postalCode: value },
        });
        break;
      case 'notes':
        setProperty({
          ...prevProperty,
          data: {
            ...prevProperty.data,
            settings: { ...prevProperty.data.settings, notes: value },
          },
        });
        break;
    }
  };

  const handleSubmit = () => {
    if (!property) return;

    const updatedProperty: Property = {
      ...property,
      id: Number(propertyID),
      tenantID: state.tenant.id,
      externalID: property?.externalID || '',
      data: {
        ...property.data,
        timeZone: timeZone?.timeZone || '',
        requireTagPhoto: property?.data.requireTagPhoto ?? true,
      },
    };
    fetcher.submit(
      {
        body: JSON.stringify(updatedProperty),
        handler: PropertiesActionHandler.UPDATE,
      },
      {
        method: 'PUT',
        action: routeWithParams(ROUTES.PROPERTY, [
          state.tenant.id,
          Number(propertyID),
        ]),
        encType: 'application/json',
      },
    );
  };

  const handleCancelAction = () => {
    if (isMobile) {
      navigate(routeWithParams(ROUTES.PROPERTIES, [state.tenant.id]));
    } else {
      revalidator.revalidate();
    }
  };
  const cancelButtonText = isMobile ? t('dashboard') : t('cancel');
  const cancelButtonIcon = useMemo(() => {
    return isMobile ? <ArrowBack /> : <Close />;
  }, [isMobile]);
  const handleSetPropertyType = (e: ChangeEvent<HTMLInputElement>) => {
    if (!property) return;
    setProperty({ ...property, type: e.target.value as PropertyType });
  };
  const handleToggleDisableFindMyCar = (value: boolean) => {
    if (!property) return;
    setProperty({
      ...property,
      data: { ...property.data, disableFindMyCar: value },
    });
  };

  const handleToggleDisableTowing = (value: boolean) => {
    if (!property) return;
    setProperty({
      ...property,
      data: { ...property.data, disableTowing: value },
    });
  };

  const handleToggleEnablePatrols = (value: boolean) => {
    if (!property) return;
    setProperty({
      ...property,
      data: {
        ...property.data,
        settings: { ...property.data.settings, enablePatrols: value },
      },
    });
  };

  const handleSetFilterLabel = (filterLabels: string[]) => {
    if (!property) return;
    setProperty({
      ...property,
      data: {
        ...property.data,
        filterLabels: filterLabels,
      },
    });
  };

  const handleSetNotifyPM = (jobTypes: JobType[]) => {
    if (!property) return;
    setProperty({
      ...property,
      data: {
        ...property.data,
        settings: { ...property.data.settings, notifyPM: jobTypes },
      },
    });
  };
  const handleSetRequirePMApproval = (jobTypes: JobType[]) => {
    if (!property) return;
    setProperty({
      ...property,
      data: {
        ...property.data,
        settings: { ...property.data.settings, requirePMApproval: jobTypes },
      },
    });
  };

  const handleToggleNotifyPMPendingPermit = (value: boolean) => {
    if (!property) return;
    setProperty({
      ...property,
      data: {
        ...property.data,
        settings: { ...property.data.settings, notifyPMPendingPermit: value },
      },
    });
  };
  const handleToggleRequireTagPhoto = (value: boolean) => {
    if (!property) return;
    setProperty({
      ...property,
      data: { ...property.data, requireTagPhoto: value },
    });
  };

  const disableFindMyCarLink = useMemo(() => {
    if (tenantHasFindMyCarDisabled) return '';
    return property?.data.disableFindMyCar
      ? ''
      : `${window.FIND_MY_CAR_BASE_URL}/?pid=${property?.id}&tid=${state.tenant.id}`;
  }, [property, state.tenant.id]);

  return (
    <ValidatorForm onSubmit={handleSubmit}>
      <FormFrame loading={fetcher.state !== 'idle'}>
        <DetailsHeader
          title={t('details')}
          onCancel={handleCancelAction}
          submitLabel={t('buttons.update', {
            name: t('property.label', { ns: 'entities' }),
          })}
          cancelButtonLabel={cancelButtonText}
          cancelButtonIcon={cancelButtonIcon}
          readonly={!state.entityPermissions.property.update}
          disableSubmit={fetcher.state !== 'idle'}
        />
        <ScrollableFormContainer
          maxHeight='calc(100dvh - 454px)'
          mobileMaxHeight={254 + TAB_LAYOUT_HEADER_HEIGHT}
        >
          <SettingsSection title={''}>
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaTextField
                  name='name'
                  label={t('property.name', { ns: 'entities' })}
                  value={property?.name || ''}
                  onChange={handleChange}
                  required
                  resolve={_property}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='description'
                  label={t('property.description', { ns: 'entities' })}
                  value={property?.description || ''}
                  onChange={handleChange}
                  resolve={_property}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaDropdown
                  label={t('property.type', { ns: 'entities' })}
                  required
                  name='type'
                  options={[
                    PropertyType.Residential,
                    PropertyType.Commercial,
                    PropertyType.Apartments,
                  ]}
                  value={property?.type || PropertyType.Residential}
                  setValue={handleSetPropertyType}
                  resolve={_property}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <TimeZoneSelect
                  name='timeZone'
                  inputLabel={t('property.data.timeZone', { ns: 'entities' })}
                  value={timeZone || TIME_ZONES[0]}
                  setValue={setTimeZone}
                  fullWidth
                  resolve={_property}
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='externalID'
                  label={t('property.externalID', { ns: 'entities' })}
                  value={property?.externalID || ''}
                  onChange={handleChange}
                  resolve={_property}
                  readonly={!AuthService.IsSuperAdmin()}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaDropdown
                  name='status'
                  label={t('property.status', { ns: 'entities' })}
                  value={property?.status || Status.Active}
                  setValue={(e: ChangeEvent<HTMLInputElement>) => {
                    if (property) {
                      setProperty({
                        ...property,
                        status: e.target.value as Status,
                      });
                    }
                  }}
                  options={[Status.Active, Status.Disabled]}
                  resolve={_property}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  label={t('property.data.settings.gateCode', {
                    ns: 'entities',
                  })}
                  name={t('property.data.settings.gateCode', {
                    ns: 'entities',
                  })}
                  value={property?.data.settings.gateCode || ''}
                  onChange={({ target: { value: v } }) => {
                    if (property) {
                      setProperty({
                        ...property,
                        data: {
                          ...property.data,
                          settings: {
                            ...property.data.settings,
                            gateCode: v,
                          },
                        },
                      });
                    }
                  }}
                  resolve={_property}
                />
              </Grid>
              <Grid size={12}>
                <AuturaTextField
                  name='notes'
                  label={t('property.data.settings.notes', { ns: 'entities' })}
                  value={property?.data?.settings?.notes || ''}
                  onChange={handleChange}
                  resolve={_property}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('subheadings.location', { ns: 'properties' })}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='street'
                  label={t('property.location.street', { ns: 'entities' })}
                  value={property?.location?.street || ''}
                  onChange={handleChange}
                  required
                  resolve={_property}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='city'
                  label={t('property.location.city', { ns: 'entities' })}
                  value={property?.location?.city || ''}
                  onChange={handleChange}
                  required
                  resolve={_property}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaStateField
                  stateCode={property?.location?.stateCode || ''}
                  setStateCode={(stateCode: string) => {
                    if (property) {
                      setProperty({
                        ...property,
                        location: { ...property.location, stateCode },
                      });
                    }
                  }}
                  label={t('property.location.stateCode', { ns: 'entities' })}
                  required
                  resolve={_property}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='postalCode'
                  label={t('property.location.postalCode', { ns: 'entities' })}
                  value={property?.location?.postalCode || ''}
                  onChange={handleChange}
                  required
                  resolve={_property}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('subheadings.settings', {
              ns: 'properties',
            })}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaSwitch
                  label={t('property.data.disableTowing', {
                    ns: 'entities',
                  })}
                  name={'property.data.disableTowing'}
                  value={property?.data.disableTowing || false}
                  setValue={handleToggleDisableTowing}
                  disabled={fetcher.state !== 'idle'}
                  readonly={!state.entityPermissions.property.update}
                  helperText={t('helpers.disable_tows', {
                    ns: 'properties',
                  })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaSwitch
                  label={t('property.data.settings.enablePatrols', {
                    ns: 'entities',
                  })}
                  name={'property.data.settings.enablePatrols'}
                  value={property?.data?.settings?.enablePatrols ?? true}
                  setValue={handleToggleEnablePatrols}
                  disabled={fetcher.state !== 'idle'}
                  readonly={!state.entityPermissions.property.update}
                  helperText={t('helpers.enable_patrols', {
                    ns: 'properties',
                  })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaSwitch
                  label={t('property.data.disableFindMyCar', {
                    ns: 'entities',
                  })}
                  name={'property.data.disableFindMyCar'}
                  value={property?.data.disableFindMyCar || false}
                  setValue={handleToggleDisableFindMyCar}
                  disabled={
                    fetcher.state !== 'idle' ||
                    !state.tenant?.data?.enableFindMyCar
                  }
                  readonly={!state.entityPermissions.property.update}
                  helperText={
                    !state.tenant?.data?.enableFindMyCar
                      ? t('helpers.disable_fmc_tenant_disabled', {
                          ns: 'properties',
                        })
                      : t('helpers.disable_fmc', {
                          ns: 'properties',
                        })
                  }
                  link={disableFindMyCarLink}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaSwitch
                  label={t('property.data.settings.notifyPMPendingPermit', {
                    ns: 'entities',
                  })}
                  name={'property.data.settings.notifyPMPendingPermit'}
                  value={property?.data?.settings?.notifyPMPendingPermit ?? false}
                  setValue={handleToggleNotifyPMPendingPermit}
                  disabled={fetcher.state !== 'idle'}
                  readonly={!state.entityPermissions.property.update}
                  helperText={t('helpers.notify_pending_permit', {
                    ns: 'properties',
                  })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaSwitch
                  label={t('labels.require_tag_photo', {
                    ns: 'properties',
                  })}
                  name={'property.data.requireTagPhoto'}
                  value={
                    typeof property?.data?.requireTagPhoto === 'boolean'
                      ? property?.data?.requireTagPhoto
                      : true
                  }
                  setValue={handleToggleRequireTagPhoto}
                  disabled={fetcher.state !== 'idle'}
                  readonly={!state.entityPermissions.property.update}
                  helperText={t('helpers.require_tag_photo', {
                    ns: 'properties',
                  })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
                sx={{ pl: {xs: 0, lg: 7.5} }}
              >
                <JobTypeChecklist
                  label={t('property.data.settings.notifyPM', {
                    ns: 'entities',
                  })}
                  jobTypes={property?.data?.settings?.notifyPM || []}
                  enabledJobTypes={[JobType.TOW, JobType.PATROL, JobType.TAG]}
                  setJobTypes={handleSetNotifyPM}
                  helperText={t('helpers.notify_manager', {
                    ns: 'properties',
                  })}
                />
              </Grid>


              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
                sx={{ pl: {xs: 0, lg: 7.5} }}
              >
                <JobTypeChecklist
                  label={t('property.data.settings.requirePMApproval', {
                    ns: 'entities',
                  })}
                  jobTypes={property?.data?.settings?.requirePMApproval || []}
                  enabledJobTypes={[JobType.TOW, JobType.TAG]}
                  setJobTypes={handleSetRequirePMApproval}
                  helperText={t('helpers.require_signature', {
                    ns: 'properties',
                  })}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          {!isMobile && (
            <SettingsSection
              title={t('property.data.filterLabels.label_plural', {
                ns: 'entities',
              })}
              subtitle={t('helpers.filter_labels', {
                ns: 'properties',
              })}
              divider={false}
            >
              <Grid container width={'100%'} spacing={1.5}>
                <Grid
                  size={{
                    xs: 12,
                  }}
                >
                  <FilterLabelAutocomplete
                    filterLabels={property?.data.filterLabels || []}
                    setFilterLabels={handleSetFilterLabel}
                  />
                </Grid>
              </Grid>
            </SettingsSection>
          )}
        </ScrollableFormContainer>
      </FormFrame>
    </ValidatorForm>
  );
};

export default PropertyDetails;
